import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplets, Leaf, AlertCircle } from 'lucide-react';
import type { Plant } from '@/types';
import { useTelegramHaptic } from '@/hooks/telegram';
import { useStore } from '@/hooks/useStore';
import { photoSrc } from '@/utils/api';
import PlantAvatar from './PlantAvatar';
import WateringProgress from './WateringProgress';

interface PlantCardProps {
  plant: Plant;
}

const personalityEmojis: Record<string, string> = {
  FRIENDLY: '😊',
  SHY: '😳',
  DRAMATIC: '🎭',
  WISE: '🧘',
  PLAYFUL: '🎉',
};

export default function PlantCard({ plant }: PlantCardProps) {
  const navigate = useNavigate();
  const haptic = useTelegramHaptic();
  const { waterPlant, unreadCounts } = useStore();
  const [isWatering, setIsWatering] = useState(false);
  const unreadCount = unreadCounts[plant.id] || 0;
  const status = (plant.wateringStatus?.status || 'ok') as 'ok' | 'soon' | 'overdue' | 'critical';
  const [photoError, setPhotoError] = useState(false);

  const handleClick = () => {
    haptic('selection');
    navigate(`/plant/${plant.id}`);
  };

  const handleQuickWater = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isWatering) return;

    haptic('medium');
    setIsWatering(true);

    try {
      await waterPlant(plant.id);
      haptic('success');
    } catch {
      haptic('error');
    } finally {
      setIsWatering(false);
    }
  };

  const showWaterButton = status === 'soon' || status === 'overdue' || status === 'critical';

  return (
    <div
      onClick={handleClick}
      className="plant-card tg-card cursor-pointer"
    >
      <div className="flex items-start gap-4">
        {/* Plant photo or SVG avatar */}
        <div className="relative flex-shrink-0">
          {plant.photoUrl && !photoError ? (
            <div className="w-16 h-16 rounded-2xl overflow-hidden">
              <img src={photoSrc(plant.photoUrl)} alt={plant.nickname} className="w-full h-full object-cover" onError={() => setPhotoError(true)} />
            </div>
          ) : (
            <PlantAvatar
              speciesScientificName={plant.species?.scientificName}
              category={plant.species?.category}
              size="md"
              status={status}
            />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-[var(--sf-card-bg,white)]" />
          )}
        </div>

        {/* Plant info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg truncate">{plant.nickname}</h3>
            <span className="text-lg">{personalityEmojis[plant.personality]}</span>
          </div>

          <p className="text-sm text-tg-hint truncate mb-2">
            {plant.species?.commonNameRu || plant.customSpecies || 'Неизвестный вид'}
          </p>

          {/* Watering progress bar */}
          <WateringProgress
            status={status}
            daysUntil={plant.wateringStatus?.daysUntil}
            message={plant.wateringStatus?.message}
            wateringFrequencyDays={plant.species?.wateringFrequencyDays}
            compact
          />

          {/* Fertilizing indicator */}
          {plant.fertilizingStatus && plant.fertilizingStatus.status !== 'none' && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${
              plant.fertilizingStatus.status === 'overdue' ? 'text-orange-500' :
              plant.fertilizingStatus.status === 'soon' ? 'text-yellow-500' :
              'text-tg-hint'
            }`}>
              <Leaf className="w-3 h-3" />
              <span>{plant.fertilizingStatus.message}</span>
            </div>
          )}

          {/* Other overdue care schedule indicators */}
          {plant.careSchedules && (() => {
            const overdueSchedules = plant.careSchedules.filter((s) => {
              if (!s.isEnabled || !s.nextDueAt) return false;
              if (s.actionType === 'WATERING' || s.actionType === 'FERTILIZING') return false;
              return new Date(s.nextDueAt).getTime() <= Date.now();
            });
            if (overdueSchedules.length === 0) return null;
            const actionEmojis: Record<string, string> = {
              MISTING: '💨', ROTATING: '🔄', REPOTTING: '🪴',
              PRUNING: '✂️', CLEANING: '🧹',
            };
            return (
              <div className="flex items-center gap-1 mt-1 text-xs text-orange-500">
                <AlertCircle className="w-3 h-3" />
                <span>{overdueSchedules.map((s) => actionEmojis[s.actionType] || '📝').join(' ')}</span>
              </div>
            );
          })()}
        </div>

        {/* Quick water button */}
        {showWaterButton && (
          <button
            onClick={handleQuickWater}
            disabled={isWatering}
            className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 active:scale-90 ${
              isWatering ? 'opacity-50' : ''
            }`}
            style={{
              background: status === 'critical' ? 'rgba(239, 68, 68, 0.15)' :
                          status === 'overdue' ? 'rgba(249, 115, 22, 0.15)' :
                          'rgba(59, 130, 246, 0.15)',
            }}
          >
            <Droplets className={`w-5 h-5 ${
              status === 'critical' ? 'text-red-500' :
              status === 'overdue' ? 'text-orange-500' :
              'text-blue-500'
            } ${isWatering ? 'animate-pulse' : ''}`} />
          </button>
        )}
      </div>

    </div>
  );
}
